<template>
  <BaseDialog
    v-model="showTagListForm"
    :actionAllow="false"
    title="Document List"
    cardTextClass="pa-0 ma-0"
    width="70vw"
  >
    <template #information>
      <v-form
        v-model="isValid"
        ref="documentForm"
        class="content_background pa-5"
      >
        <v-row class="ma-0">
          <v-col cols="3" v-if="permissions && permissions.add">
            <SelectField
              id="document_type"
              name="document_type"
              :itemsList="documentTypeList"
              itemText="name"
              itemValue="id"
              dense
              :isClearable="true"
              :isSearchRequired="true"
              label="Document Type*"
              :rules="[(val) => !!val || 'Document type is required']"
              v-model="documentForm.document_type"
              :error-messages="formErrors.document_type"
              @input="delete formErrors.document_type"
            />
          </v-col>
          <v-col cols="3" v-if="permissions && permissions.add">
            <DatePicker
              id="expiry_date"
              name="expiry_date"
              type="date"
              :isClearable="true"
              :min="returnToday()"
              dense
              outlined
              label="Expiry Date*"
              :rules="[(val) => !!val || 'Expiry Date is required']"
              v-model="documentForm.expiry_date"
              :error-messages="formErrors.expiry_date"
              @input="delete formErrors.expiry_date"
            />
          </v-col>
          <v-col cols="2" v-if="permissions && permissions.add">
            <FileField
              id="document"
              name="document"
              :isClearable="true"
              dense
              accept="image/*"
              :truncateLength="10"
              label="Document*"
              :rules="[(val) => !!val || 'Document is required']"
              v-model="documentForm.document"
              :error-messages="formErrors.document"
              @input="delete formErrors.document"
            />
          </v-col>
          <v-col cols="3" v-if="permissions && permissions.add">
            <InputField
              id="description"
              name="description"
              :isClearable="true"
              dense
              label="Description"
              v-model="documentForm.description"
              :error-messages="formErrors.description"
              @input="delete formErrors.description"
            />
          </v-col>
          <v-col cols="1" class="mt-1" v-if="permissions && permissions.add">
            <PrimaryButton
              id="documentListAdd"
              @click="submitForm"
              :disabled="!isValid"
              name="Add"
            >
            </PrimaryButton>
          </v-col>
          <v-col cols="12" class="app-relative pa-0 ma-0">
            <v-btn-toggle
              dense
              v-model="toggleView"
              mandatory
              active-class="primary"
              class="pa-1 position-absolute"
            >
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-btn
                    :value="0"
                    v-on="on"
                    v-bind="attrs"
                    color="secondary lighten-1"
                    active-class="primary"
                  >
                    <v-icon color="white">mdi-table</v-icon>
                  </v-btn>
                </template>
                <span>Table View</span>
              </v-tooltip>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-btn
                    :value="1"
                    v-on="on"
                    v-bind="attrs"
                    color="secondary lighten-1"
                    active-class="primary"
                  >
                    <v-icon color="white">mdi-image</v-icon>
                  </v-btn>
                </template>
                <span>Grid View</span>
              </v-tooltip>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" class="pa-0 border-around mt-4" v-if="toggleView == 0">
            <ListLayout
              :gridStyle="{ height: '45vh', 'overflow-y': 'auto' }"
              :context="context"
              title="Document List"
              :hasAfterTitle="false"
              :hasFilter="false"
              :isColumnShowVisibility="false"
              :tableData="tableData"
              :total="totalItems"
              :tableHeader="tableHeader"
              :noMargin="true"
            >
            </ListLayout>
          </v-col>
          <v-col cols="12" v-else class="mt-5 pt-16">
            <v-row v-if="tableData.length > 0" style="height: 51vh">
              <v-col cols="3" v-for="(item, i) in tableData" :key="i">
                <v-card elevation="0" outlined class="pa-2 app-relative">
                  <v-card-title
                    class="pa-2 w-100 white"
                    style="position: absolute; top: 0; right: 0; z-index: 1"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="text-caption" v-bind="attrs" v-on="on">{{
                          item.document_type
                        }}</span>
                      </template>
                      <span class="text-capitalize">Document Type </span>
                    </v-tooltip>

                    <v-spacer></v-spacer>
                    <v-tooltip
                      top
                      :color="item.days_countdown <= 0 ? 'error' : ''"
                    >
                      <template #activator="{ on, attrs }">
                        <v-icon
                          :color="
                            item.days_countdown < 15 && item.days_countdown > 0
                              ? 'warning'
                              : 'error'
                          "
                          v-on="on"
                          v-bind="attrs"
                        >
                          mdi-information
                        </v-icon>
                      </template>
                      <span>{{ messageToDisplay(item.days_countdown) }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          color="error"
                          v-if="permissions && permissions.delete"
                          v-on="on"
                          v-bind="attrs"
                          @click="deleteItem(item.id)"
                        >
                          mdi-delete
                        </v-icon>
                      </template>
                      <span>Delete</span>
                    </v-tooltip>
                  </v-card-title>
                  <a :href="item.document" target="_blank">
                    <v-img :src="item.document" height="180px" width="300px" />
                  </a>
                  <v-card-title
                    class="py-2 w-100 white"
                    style="
                      position: absolute;
                      bottom: 0;
                      right: 0;
                      z-index: 1;
                      opacity: 0.8;
                    "
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          class="text-caption text-center w-100"
                          v-bind="attrs"
                          v-on="on"
                          >{{ item.expiry_date }}</span
                        >
                      </template>
                      <span class="text-capitalize">Expiry Date </span>
                    </v-tooltip>
                  </v-card-title>
                </v-card>
              </v-col>
            </v-row>
            <div
              v-else
              style="height: 48vh"
              class="d-flex align-center justify-center white"
            >
              <h6 class="text-grey">There is no document available.</h6>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from "@/components/BaseComponents/BaseDialog.vue";
import ListLayout from "@/components/LayoutComponents/ListLayout.vue";
import { bus } from "@/main";
import DocumentFormButton from "@/components/Button/DocumentFormButton.vue";
import { generatedFormData, returnToday } from "@/utils/functions";

export default {
  components: {
    BaseDialog,
    ListLayout,
    DocumentFormButton,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    parentObj: {
      type: Object,
    },
    moduleType: {
      type: String,
    },
  },
  data() {
    return {
      returnToday,

      tableHeader: [
        {
          headerName: "Document Type",
          field: "document_type",
          sortable: false,
          resizable: false,
        },
        {
          headerName: "Expiry Date",
          field: "expiry_date",
          sortable: false,
          resizable: false,
        },
        {
          headerName: "Actions",
          field: "actions",
          sortable: false,
          resizable: false,
          cellRenderer: "DocumentFormButton",
        },
      ],
      tableData: [],

      totalItems: 0,
      documentTypeList: [],

      documentForm: {},
      formErrors: {},

      toggleView: 0,
      isValid: true,
    };
  },
  computed: {
    formTitle() {
      if (
        this.parentObj &&
        this.parentObj.vehicle_details &&
        this.parentObj.vehicle_details.vehicle_number &&
        this.moduleType == "Vehicle"
      ) {
        return this.parentObj.vehicle_details.vehicle_number;
      } else if (this.parentObj && this.moduleType == "Driver") {
        return this.parentObj.full_name;
      } else {
        return "";
      }
    },
    showTagListForm: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    context() {
      return { parentComponent: this };
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.getDocumentTypeList();
      } else {
        this.resetForm();
        this.$emit("getList");
      }
    },
  },
  methods: {
    messageToDisplay(daysLeft) {
      return daysLeft < 15 && daysLeft > 0
        ? `This document will expire in ${daysLeft} days`
        : "This Document has expired";
    },
    deleteItem(id) {
      this.$root
        .$confirm("Delete?", "Are you sure you want to delete document?", {
          color: "error",
        })
        .then(async (confirm) => {
          if (confirm) {
            if (this.moduleType == "Vehicle") {
              this.deleteVehicleDocuments(id);
            }
            if (this.moduleType == "Driver") {
              this.deleteDriverDocuments(id);
            }
          }
        });
    },
    getDocumentTypeList() {
      let obj = {};
      if (this.moduleType == "Vehicle") {
        obj.search = "vehicle document";
        this.getVehicleDocumentsList();
      }
      if (this.moduleType == "Driver") {
        obj.search = "driver document";
        this.getDriverDocumentsList();
      }
      this.getFieldConfiguration(obj, "documentTypeList");
    },
    submitForm() {
      if (this.moduleType == "Vehicle") {
        this.addDocumentForVehicle();
      }
      if (this.moduleType == "Driver") {
        this.addDocumentForDriver();
      }
    },
    resetForm() {
      this.documentForm = {};
      this.formErrors = {};
      this.$refs.documentForm.resetValidation();
    },
    addDocumentForVehicle() {
      let payload = generatedFormData({ ...this.documentForm });
      payload.append("vehicle", this.parentObj.id);
      bus.$emit("showLoader", true);
      this.$api.vehicleDocuments
        .createVehicleDocumentsObject(payload)
        .then(() => {
          bus.$emit("showLoader", false);
          bus.$emit("showToastMessage", {
            message: "Document Uploaded successfully!",
            color: "success",
          });
          this.resetForm();
          this.getVehicleDocumentsList();
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
          bus.$emit("showToastMessage", {
            message: "Couldn't upload document!",
            color: "error",
          });
          this.formErrors = err.data;
        });
    },
    addDocumentForDriver() {
      let payload = generatedFormData({ ...this.documentForm });
      payload.append("driver", this.parentObj.id);
      bus.$emit("showLoader", true);
      this.$api.driverDocuments
        .createDriverDocumentsObject(payload)
        .then(() => {
          bus.$emit("showLoader", false);
          bus.$emit("showToastMessage", {
            message: "Document Uploaded successfully!",
            color: "success",
          });
          this.resetForm();
          this.getDriverDocumentsList();
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
          bus.$emit("showToastMessage", {
            message: "Couldn't upload document!",
            color: "error",
          });
          this.formErrors = err.data;
        });
    },
    async getVehicleDocumentsList() {
      bus.$emit("showLoader", true);
      await this.$api.vehicleDocuments
        .getVehicleDocumentsList({ limit: "all", vehicle: this.parentObj.id })
        .then((res) => {
          this.tableData = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    async getDriverDocumentsList() {
      bus.$emit("showLoader", true);
      await this.$api.driverDocuments
        .getDriverDocumentsList({ limit: "all", driver: this.parentObj.id })
        .then((res) => {
          this.tableData = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    deleteVehicleDocuments(id) {
      bus.$emit("showLoader", true);
      this.$api.vehicleDocuments
        .deleteVehicleDocumentsObject(id)
        .then(() => {
          this.getVehicleDocumentsList();
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    deleteDriverDocuments(id) {
      bus.$emit("showLoader", true);
      this.$api.driverDocuments
        .deleteDriverDocumentsObject(id)
        .then(() => {
          this.getDriverDocumentsList();
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getFieldConfiguration(params = {}, objKey) {
      bus.$emit("showLoader", true);
      params = {
        ...params,
        limit: "all",
      };
      this.$api.fieldConfiguration
        .getFieldConfigurationList(params)
        .then((res) => {
          this[objKey] = res.data.map((obj) => obj.name);
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
  },
  created() {
    this.permissions = this.$permissions.getPermission("fleet");
  },
};
</script>

<style scoped lang="scss">
.border-around{
  border: 1px solid var(--v-primary-lighten1);
}
.position-absolute {
  position: absolute;
  right: 10px;
  top: 25px;
  z-index: 1;
}
</style>
